// © Copyright 2026 Emman Consulting SAS
import React, { useState, useRef, useEffect, useMemo } from "react";
import { PANINI, FWC, CC_PLAYERS, GROUPS, TEAMS, GROUP_THEMES, sk } from "../../data/albumData.js";
import { I18N } from "../../i18n.js";
import { vibrate } from "../../utils/auth.js";
import FlagImg from "../ui/FlagImg.jsx";

const DupesListSection = React.memo(function DupesListSection({ col, onTap, onReset, onDecrementDupe, lang }) {
  const t = I18N[lang] || I18N.es;

  const filtered = useMemo(() => {
    const out = [];
    PANINI.forEach((s) => {
      const st = col[s.code];
      if (st?.quantity > 0) out.push({ ...s, quantity: st.quantity, section: "Panini", subsection: t.logoOficialTitle });
    });
    FWC.forEach((s) => {
      const st = col[s.code];
      if (st?.quantity > 0) out.push({ ...s, quantity: st.quantity, section: t.fwcTitle, subsection: t.fwcSub });
    });
    CC_PLAYERS.forEach((s) => {
      const st = col[s.code];
      if (st?.quantity > 0) out.push({ ...s, quantity: st.quantity, section: t.ccTitle, subsection: t.ccSub });
    });
    GROUPS.forEach((g) => {
      g.teams.forEach((tc) => {
        const td = TEAMS[tc];
        if (!td) return;
        const tname = lang === "en" ? (td.en || td.n) : td.n;
        for (let i = 1; i <= 20; i++) {
          const key = sk(tc, i);
          const st = col[key];
          if (st?.quantity > 0) {
            out.push({
              code: key, label: tname, quantity: st.quantity,
              section: `${t.group} ${g.id}`, subsection: tname,
              country: td.cc, groupTheme: GROUP_THEMES[g.id],
            });
          }
        }
      });
    });
    return out;
  }, [col, lang]);

  const timerRef = useRef(null);
  const [isLongPress, setIsLongPress] = useState(false);
  const [longPressCode, setLongPressCode] = useState(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handlePressStart = (e, code) => {
    setIsLongPress(false);
    setLongPressCode(code);
    timerRef.current = setTimeout(() => {
      setIsLongPress(true);
      vibrate(50);
      onReset(code);
    }, 400);
  };

  const handlePressEnd = (e, code) => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    if (longPressCode === code) setLongPressCode(null);
    if (!isLongPress) onDecrementDupe(code);
  };

  const handlePressMove = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    setLongPressCode(null);
  };

  return (
    <div className="dupes-list">
      {filtered.length === 0 && (
        <div className="dupes-empty">No tienes repetidas</div>
      )}
      {filtered.map((item, idx) => {
        const prevSection = idx > 0 ? filtered[idx - 1].section : null;
        const showSectionHeader = item.section !== prevSection;
        const prevSubsection = idx > 0 ? filtered[idx - 1].subsection : null;
        const showSubsectionHeader = showSectionHeader || item.subsection !== prevSubsection;
        return (
          <div key={item.code}>
            {showSectionHeader && (
              <div className="dupes-section-header">{item.section}</div>
            )}
            {showSubsectionHeader && (
              <div className="dupes-subsection-header">
                {item.country && <FlagImg cc={item.country} style={{ width: 16, height: 12, marginRight: 8 }} />}
                {item.subsection}
              </div>
            )}
            <div className={"dupes-item" + (longPressCode === item.code ? " pressing" : "")}>
              <div className="dupes-code">{item.code}</div>
              <div className="dupes-qty">×{item.quantity}</div>
              <button
                className="dupes-remove"
                onMouseDown={(e) => handlePressStart(e, item.code)}
                onMouseUp={(e) => handlePressEnd(e, item.code)}
                onMouseLeave={handlePressMove}
                onTouchStart={(e) => handlePressStart(e, item.code)}
                onTouchEnd={(e) => handlePressEnd(e, item.code)}
                onTouchMove={handlePressMove}
              >−</button>
            </div>
          </div>
        );
      })}
    </div>
  );
});

export default DupesListSection;
